import { useLocation } from 'react-router-dom';
import type { Dict, Lang } from '@/i18n';
import { DEFAULT_LANG, dictFor, isLang } from '@/i18n';

/**
 * La langue est portée par `?lang=fr`, pas par un état React.
 *
 * C'est ce qui permet au rendu SSR de servir la page déjà traduite et à Google
 * d'indexer les deux versions (voir les `hreflang` dans `seo.ts`). Un simple
 * `useState` aurait donné une page anglaise dans le HTML servi, quelle que soit
 * la langue affichée ensuite.
 */
export function useLang(): Lang {
  const { search } = useLocation();
  const value = new URLSearchParams(search).get('lang');
  return isLang(value) ? value : DEFAULT_LANG;
}

export function useT(): Dict {
  return dictFor(useLang());
}

/** Même chemin, langue inversée — pour le bouton EN/FR. */
export function otherLangHref(pathname: string, search: string, lang: Lang): string {
  const params = new URLSearchParams(search);
  const next: Lang = lang === 'en' ? 'fr' : 'en';
  if (next === DEFAULT_LANG) {
    params.delete('lang');
  } else {
    params.set('lang', next);
  }
  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

/**
 * La query string à recopier d'un lien interne au suivant : `?lang=xx`, et
 * rien d'autre.
 *
 * Propager `location.search` en entier est tentant — c'est une ligne de moins —
 * mais `/welcome` est atteint avec `?session_id=cs_live_…` collé par Stripe.
 * Recopié tel quel, cet identifiant suivrait le visiteur sur `/blog`,
 * `/privacy`, `/terms`, puis partirait dans l'en-tête `Referer` de la première
 * ressource tierce chargée par ces pages, et dans l'URL qu'il partage ou met
 * en favori. On ne garde donc que ce qui a une raison de voyager : la langue.
 */
export function langSearch(search: string): string {
  const value = new URLSearchParams(search).get('lang');
  return isLang(value) ? `?lang=${value}` : '';
}

/** `withLang('/blog')` → `/blog?lang=fr` si on lit la page en français. */
export function useLangHref(): (path: string) => string {
  const query = langSearch(useLocation().search);
  return (path: string) => `${path}${query}`;
}
